'use client';

import { Button } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { type ComponentProps, createContext, type HTMLAttributes, useContext, useEffect, useRef, useState } from 'react';
import { type BundledLanguage, codeToHtml, type ShikiTransformer } from 'shiki';

type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language: BundledLanguage;
  showLineNumbers?: boolean;
};

type CodeBlockContextType = {
  code: string;
};

const CodeBlockContext = createContext<CodeBlockContextType>({
  code: '',
});

const lineNumberTransformer: ShikiTransformer = {
  name: 'line-numbers',
  line(node, line) {
    node.children.unshift({
      type: 'element',
      tagName: 'span',
      properties: {
        className: ['inline-block', 'min-w-10', 'mr-4', 'text-right', 'select-none', 'text-muted-foreground'],
      },
      children: [{ type: 'text', value: String(line) }],
    });
  },
};

export async function highlightCode(code: string, language: BundledLanguage, showLineNumbers = false) {
  const transformers: ShikiTransformer[] = showLineNumbers ? [lineNumberTransformer] : [];

  return await Promise.all([
    codeToHtml(code, {
      lang: language,
      theme: 'one-light',
      transformers,
    }),
    codeToHtml(code, {
      lang: language,
      theme: 'one-dark-pro',
      transformers,
    }),
  ]);
}

export const CodeBlock = ({ code, language, showLineNumbers = false, className, children, ...props }: CodeBlockProps) => {
  const [html, setHtml] = useState<string>('');
  const [darkHtml, setDarkHtml] = useState<string>('');
  const mounted = useRef(false);

  useEffect(() => {
    highlightCode(code, language, showLineNumbers).then(([light, dark]) => {
      if (!mounted.current) {
        setHtml(light);
        setDarkHtml(dark);
        mounted.current = true;
      }
    });

    return () => {
      mounted.current = false;
    };
  }, [code, language, showLineNumbers]);

  // Base classes for code block styling with improved overflow handling
  // We keep white-space: pre for code formatting but allow wrapping in long strings
  const codeBlockClasses = cn(
    'dark:hidden',
    '[&>pre]:m-0',
    '[&>pre]:bg-background!',
    '[&>pre]:p-4',
    '[&>pre]:text-foreground!',
    '[&>pre]:text-sm',
    '[&>pre]:overflow-x-auto', // Allow horizontal scroll as fallback
    '[&>pre]:max-w-full', // Ensure pre doesn't exceed container
    '[&_code]:font-mono',
    '[&_code]:text-xs',
    '[&_code]:block',
    '[&_code]:w-full',
    // Force wrapping for long strings while preserving code structure
    '[&_code_.string]:break-all', // Break long string values
    '[&_code_.string]:overflow-wrap-anywhere', // Break anywhere if needed
    '[&_code_.string]:whitespace-pre-wrap', // Allow wrapping in strings
    // Also handle other token types that might have long content
    '[&_code_span[style*="color"]]:break-words', // Break words in colored spans
    '[&_code_span[style*="color"]]:overflow-wrap-anywhere'
  );

  const darkCodeBlockClasses = cn(
    'hidden',
    'dark:block',
    '[&>pre]:m-0',
    '[&>pre]:bg-background!',
    '[&>pre]:p-4',
    '[&>pre]:text-foreground!',
    '[&>pre]:text-sm',
    '[&>pre]:overflow-x-auto',
    '[&>pre]:max-w-full',
    '[&_code]:font-mono',
    '[&_code]:text-xs',
    '[&_code]:block',
    '[&_code]:w-full',
    '[&_code_.string]:break-all',
    '[&_code_.string]:overflow-wrap-anywhere',
    '[&_code_.string]:whitespace-pre-wrap',
    '[&_code_span[style*="color"]]:break-words',
    '[&_code_span[style*="color"]]:overflow-wrap-anywhere'
  );

  return (
    <CodeBlockContext.Provider value={{ code }}>
      <div
        className={cn(
          'group relative w-full rounded-md border bg-background text-foreground',
          'overflow-hidden', // Hide overflow on container
          className
        )}
        {...props}
      >
        <div className='relative'>
          <div
            className={codeBlockClasses}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: Shiki returns HTML string, this is necessary for syntax highlighting
            dangerouslySetInnerHTML={{ __html: html }}
          />
          <div
            className={darkCodeBlockClasses}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: Shiki returns HTML string, this is necessary for syntax highlighting
            dangerouslySetInnerHTML={{ __html: darkHtml }}
          />
          {children && <div className='absolute top-2 right-2 z-10 flex items-center gap-2'>{children}</div>}
        </div>
      </div>
    </CodeBlockContext.Provider>
  );
};

export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};

export const CodeBlockCopyButton = ({ onCopy, onError, timeout = 2000, children, className, ...props }: CodeBlockCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const { code } = useContext(CodeBlockContext);

  const copyToClipboard = async () => {
    if (typeof window === 'undefined' || !navigator?.clipboard?.writeText) {
      onError?.(new Error('Clipboard API not available'));
      return;
    }

    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
      onCopy?.();
      setTimeout(() => setIsCopied(false), timeout);
    } catch (error) {
      onError?.(error as Error);
    }
  };

  return (
    <Button className={cn('shrink-0', className)} size='sm' onClick={copyToClipboard} variant='ghost' {...props}>
      {children ?? <Icon name={isCopied ? 'check' : 'copy'} size='sm' />}
    </Button>
  );
};
